<template>
    <Loading v-if="!didLoadAdministrators || !didLoadFaculties" />
    <v-container v-else>
        <v-row class="mr-1">   
            <v-col class="pl-0 mb-3">
                <h2 class="mb-2">Uživatelé a oprávnění</h2>
                <div v-if="!areAdministratorsEmpty" class="subtitle">
                    Vyberte uživatele, kterého chcete spravovat
                </div>
            </v-col>
            <v-col v-if="!areAdministratorsEmpty" cols="12" md="4" align-self="center" class="d-flex justify-md-end justify-start pa-0">
                <PrimaryButton @click="createNew('new')" data-cy="add-new">
                    <v-icon class="mr-1">add</v-icon>
                    Přidat uživatele
                </PrimaryButton>
            </v-col>
        </v-row>
        <v-row v-if="!areAdministratorsEmpty" class="mr-1" justify="start">
            <v-col cols="12" md="5" lg="4" class="pl-0 pb-0">
                <v-text-field
                    color="light-green darken-1"
                    v-model="search"
                    prepend-icon="search"
                    label="Koho hledáte?"
                    clearable
                />
            </v-col>
            <v-col>
                <v-container class="pa-0">
                    <v-row justify="end">
                        <v-col cols="12" md="4"></v-col>
                        <v-col cols="12" md="4">
                            <v-select 
                                color="light-green darken-1"
                                :items="faculties"
                                v-model="selectedFaculties"
                                item-title="title"
                                item-value="id"
                                label="Přiřazená práva"
                                multiple
                                clearable
                                :menu-props="{offsetY: true , maxHeight: '60vh', minHeight: '15vh'}"
                            >
                                <template v-slot:selection="{ item, index }">
                                    <span class="mr-1" v-if="index < maxDisplayOnSelect">{{ item.title }}</span>
                                    <span class="grey--text caption ml-1" v-if="index === maxDisplayOnSelect">
                                        (+{{ numberMoreFaculties }})
                                    </span>
                                </template>
                            </v-select>
                        </v-col>
                    </v-row>
                </v-container>
            </v-col>
        </v-row>
        <v-row v-if="!areAdministratorsEmpty">
            <v-col cols="12" lg="4" v-for="(entity, i) in filteredAdministrators" :key="i" class="pa-0">
                <v-card class="mr-5 mb-5" style="border: 2px solid #eeeeef" @click="goToDetailEdit(entity.id)" variant="outlined" data-cy="card-administrator">
                    <v-list>
                        <v-list-item>
                            <v-list-item-content>
                                <v-list-item-title class="mb-1 titleCard">{{ entity.fullName }}</v-list-item-title>
                                <v-list-item-subtitle class="mb-1 subTitleCard">{{ entity.xlogin }}</v-list-item-subtitle>
                                <v-list-item-subtitle class="mt-3">
                                    <v-divider></v-divider>
                                </v-list-item-subtitle>
                            </v-list-item-content>
                        </v-list-item>
                        <v-list-item>
                            <v-list-item-content>
                                <v-list-item-title class="mb-2 titleChips">Fakulty a součásti</v-list-item-title>
                                <v-list-item-subtitle>
                                    <v-card-text class="row font-weight-medium ma-0 pa-0">
                                        <v-chip
                                            v-for="(faculty, i) in entity.faculties"
                                            :key="i"
                                            size="small"
                                            class="mr-1 mb-1"
                                        >
                                            {{ faculty.localizedAttributes.cz.name }}
                                        </v-chip>
                                    </v-card-text>
                                </v-list-item-subtitle>
                            </v-list-item-content>
                        </v-list-item>
                        <v-card-actions class="mx-3 mb-2">
                            <span class="button font-weight-medium">Upravit</span>
                        </v-card-actions>
                    </v-list>
                </v-card>
            </v-col>
        </v-row>
        <v-row v-else class="text-center">
            <v-col>
                <PlaceHolder
                    :path="'/icons/no-users.png'"
                    title="Žádní uživatelé"
                    text="Zatím nebyli přidání žádní uživatelé"
                />
                <PrimaryButton class="mt-3" @click="createNew('new')" data-cy="add-new">
                    <v-icon class="mr-1">add</v-icon>
                    Přidat uživatele
                </PrimaryButton>
            </v-col>
        </v-row>
    </v-container>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import PrimaryButton from "@/components/buttons/PrimaryButton";
import Loading from "@/components/Loading";
import PlaceHolder from "@/components/news/PlaceHolder";

const store = useStore();
const router = useRouter();

const selectedFaculties = ref([]);
const search = ref('');
const maxDisplayOnSelect = ref(2);

const didLoadAdministrators = computed(() => store.getters['displaysAdmins/didLoadAdministrators']);
const didLoadFaculties = computed(() => store.getters['displaysFaculties/didLoadFaculties']);
const getAdministrators = computed(() => store.getters['displaysAdmins/getAdministrators']);
const getFaculties = computed(() => store.getters['displaysFaculties/getFaculties']);

const numberMoreFaculties = computed(() => selectedFaculties.value.length - maxDisplayOnSelect.value);

const faculties = computed(() => getFaculties.value?.map(faculty => ({
    title: faculty.text,
    id: faculty.value.id
})) || []);

const filteredAdministrators = computed(() => {
    let administrators = selectedFaculties.value.length === 0
        ? getAdministrators.value
        : getAdministrators.value.filter(obj =>
            obj.faculties.find(x => selectedFaculties.value.includes(x.id))
        );

    administrators = administrators.filter(obj =>
        !search.value ||
        obj.fullName.toUpperCase().includes(search.value.toUpperCase()) ||
        obj.xlogin.toUpperCase().includes(search.value.toUpperCase())
    );

    return administrators;
});

const areAdministratorsEmpty = computed(() => getAdministrators.value.length === 0);

const goToDetailEdit = (id) => router.push({ name: 'administratorDetail', params: { administratorId: id } });

const createNew = () => router.push({ name: 'administratorDetail', params: { administratorId: 'new' } });

onMounted(async () => {
    await store.dispatch('displaysAdmins/fetchAdministrators');
    await store.dispatch('displaysFaculties/fetchAllFaculties');
});
</script>

<style scoped>
.button {
    color: #78BE13;
    font-weight: bold;
    font-size: 19px;
}

.titleCard {
    padding-left: 0;
    padding-top: 0;
    font-weight: 500;
    line-height: 1.2;
    word-break: normal;
    font-size: 24px;
}

.subTitleCard {
    padding: 0;
    line-height: 1.2;
    font-size: 20px;
}

.titleChips {
    padding: 0;
    line-height: 1.2;
    font-size: 14px;
    font-weight: 500;
    color: #6c7278;
}

.subTitle {
    font-size: 19px;
    color: #6D7278;
}

</style>
